import type {
  Flashcard,
  FlashcardDeckOptions,
  FlashcardLearningState,
  FlashcardNote,
  FlashcardRating,
  FlashcardReview,
} from "@japa-reader/contracts";
import { fsrs, Rating, State, type Card as FsrsCard, type Grade, type StepUnit } from "ts-fsrs";
import { makeFlashcardId } from "./flashcards";

export type RatingPreview = {
  rating: FlashcardRating;
  label: "Novamente" | "Difícil" | "Bom" | "Fácil";
  due: number;
  intervalLabel: string;
};

export function previewFlashcardRatings(card: Flashcard, options: FlashcardDeckOptions, now = Date.now()): RatingPreview[] {
  if (options.scheduler === "sm2") {
    return ([1, 2, 3, 4] as FlashcardRating[]).map((rating) => {
      const outcome = legacyOutcome(card, options, rating, now);
      return { rating, label: ratingLabel(rating), due: outcome.due, intervalLabel: formatDueDifference(outcome.due - now) };
    });
  }
  return ([Rating.Again, Rating.Hard, Rating.Good, Rating.Easy] as Grade[]).map((rating) => {
    const result = nextFsrsSchedule(card, options, rating as FlashcardRating, now);
    return {
      rating: rating as FlashcardRating,
      label: ratingLabel(rating as FlashcardRating),
      due: result.card.due.getTime(),
      intervalLabel: formatDueDifference(result.card.due.getTime() - now),
    };
  });
}

export function scheduleFlashcardReview(
  card: Flashcard,
  options: FlashcardDeckOptions,
  rating: FlashcardRating,
  durationMs: number,
  now = Date.now(),
): { card: Flashcard; review: FlashcardReview } {
  if (options.scheduler === "sm2") return scheduleLegacyReview(card, options, rating, durationMs, now);
  const result = nextFsrsSchedule(card, options, rating, now);
  const updated: Flashcard = {
    ...card,
    state: fromFsrsState(result.card.state),
    due: result.card.due.getTime(),
    stability: result.card.stability,
    difficulty: result.card.difficulty,
    elapsedDays: result.card.elapsed_days,
    scheduledDays: result.card.scheduled_days,
    learningSteps: result.card.learning_steps,
    reps: result.card.reps,
    lapses: result.card.lapses,
    lastReview: result.card.last_review?.getTime() ?? now,
    updatedAt: now,
  };
  const review: FlashcardReview = {
    id: makeFlashcardId("review"),
    cardId: card.id,
    noteId: card.noteId,
    deckId: card.deckId,
    rating,
    state: card.state,
    reviewedAt: now,
    durationMs: Math.max(0, Math.round(durationMs)),
    dueBefore: card.due,
    dueAfter: updated.due,
    scheduledDaysBefore: card.scheduledDays,
    scheduledDaysAfter: updated.scheduledDays,
    elapsedDaysBefore: card.elapsedDays,
    learningStepsBefore: card.learningSteps,
    repsBefore: card.reps,
    lapsesBefore: card.lapses,
    lastReviewBefore: card.lastReview,
    stabilityBefore: card.stability,
    stabilityAfter: updated.stability,
    difficultyBefore: card.difficulty,
    difficultyAfter: updated.difficulty,
  };
  return { card: updated, review };
}

export function applyLeechRule(
  card: Flashcard,
  note: FlashcardNote | null,
  options: FlashcardDeckOptions,
  rating: FlashcardRating,
  now = Date.now(),
) {
  const threshold = Math.max(1, options.leechThreshold);
  const triggered = rating === 1 && card.lapses > 0 && card.lapses % threshold === 0;
  if (!triggered) return { card, note, triggered: false } as const;
  return {
    card: options.leechAction === "suspend" ? { ...card, suspended: true, updatedAt: now } : card,
    note: note ? {
      ...note,
      tags: note.tags.includes("leech") ? note.tags : [...note.tags, "leech"],
      updatedAt: now,
    } : null,
    triggered: true,
  } as const;
}

function scheduleLegacyReview(card: Flashcard, options: FlashcardDeckOptions, rating: FlashcardRating, durationMs: number, now: number) {
  const outcome = legacyOutcome(card, options, rating, now);
  const updated: Flashcard = { ...card, ...outcome, reps: card.reps + 1, lastReview: now, updatedAt: now };
  const review = reviewFromCards(card, updated, rating, durationMs, now);
  return { card: updated, review };
}

function legacyOutcome(card: Flashcard, options: FlashcardDeckOptions, rating: FlashcardRating, now: number): Partial<Flashcard> & Pick<Flashcard, "due" | "state" | "scheduledDays" | "learningSteps" | "lapses" | "difficulty"> {
  const day = 86_400_000;
  const ease = card.difficulty > 0 ? card.difficulty : options.startingEase;
  if (card.state === "new" || card.state === "learning" || card.state === "relearning") {
    const relearning = card.state === "relearning";
    const steps = relearning ? options.relearningStepsMinutes : options.learningStepsMinutes;
    const first = Math.max(1, steps[0] ?? 1);
    const current = Math.max(0, Math.min(card.learningSteps, Math.max(0, steps.length - 1)));
    const nextStep = steps[current + 1];
    if (rating === 1) return { state: relearning ? "relearning" : "learning", due: now + first * 60_000, scheduledDays: 0, learningSteps: 0, lapses: card.lapses, difficulty: Math.max(1.3, ease - 0.2) };
    if (rating === 2) {
      if (card.state !== "new" && steps.length > 0 && current >= steps.length - 1) {
        const days = relearning ? Math.max(1, card.scheduledDays) : options.graduatingIntervalDays;
        return { state: "review", due: now + days * day, scheduledDays: days, learningSteps: 0, lapses: card.lapses, difficulty: Math.max(1.3, ease - 0.15) };
      }
      const hardMinutes = nextStep === undefined ? first * 1.5 : (steps[current] + nextStep) / 2;
      return { state: relearning ? "relearning" : "learning", due: now + hardMinutes * 60_000, scheduledDays: 0, learningSteps: Math.min(current + 1, Math.max(0, steps.length - 1)), lapses: card.lapses, difficulty: Math.max(1.3, ease - 0.15) };
    }
    if (rating === 3 && nextStep !== undefined) return { state: relearning ? "relearning" : "learning", due: now + nextStep * 60_000, scheduledDays: 0, learningSteps: current + 1, lapses: card.lapses, difficulty: ease };
    const days = rating === 4 ? options.easyIntervalDays : relearning ? Math.max(1, card.scheduledDays) : options.graduatingIntervalDays;
    return { state: "review", due: now + days * day, scheduledDays: days, learningSteps: 0, lapses: card.lapses, difficulty: rating === 4 ? ease + 0.15 : ease };
  }

  if (rating === 1) {
    const lapseDays = Math.max(1, Math.round(card.scheduledDays * options.newInterval));
    const hasSteps = options.relearningStepsMinutes.length > 0;
    return {
      state: hasSteps ? "relearning" : "review",
      due: hasSteps ? now + Math.max(1, options.relearningStepsMinutes[0]) * 60_000 : now + lapseDays * day,
      scheduledDays: lapseDays,
      learningSteps: 0,
      lapses: card.lapses + 1,
      difficulty: Math.max(1.3, ease - 0.2),
    };
  }
  const multiplier = rating === 2 ? options.hardInterval : rating === 4 ? ease * options.easyBonus : ease;
  const interval = Math.min(options.maximumIntervalDays, Math.max(card.scheduledDays + 1, Math.round(card.scheduledDays * multiplier * options.intervalModifier)));
  return { state: "review", due: now + interval * day, scheduledDays: interval, learningSteps: 0, lapses: card.lapses, difficulty: rating === 2 ? Math.max(1.3, ease - 0.15) : rating === 4 ? ease + 0.15 : ease };
}

function reviewFromCards(before: Flashcard, after: Flashcard, rating: FlashcardRating, durationMs: number, now: number): FlashcardReview {
  return {
    id: makeFlashcardId("review"),
    cardId: before.id,
    noteId: before.noteId,
    deckId: before.deckId,
    rating,
    state: before.state,
    reviewedAt: now,
    durationMs: Math.max(0, Math.round(durationMs)),
    dueBefore: before.due,
    dueAfter: after.due,
    scheduledDaysBefore: before.scheduledDays,
    scheduledDaysAfter: after.scheduledDays,
    elapsedDaysBefore: before.elapsedDays,
    learningStepsBefore: before.learningSteps,
    repsBefore: before.reps,
    lapsesBefore: before.lapses,
    lastReviewBefore: before.lastReview,
    stabilityBefore: before.stability,
    stabilityAfter: after.stability,
    difficultyBefore: before.difficulty,
    difficultyAfter: after.difficulty,
  };
}

export function restoreCardBeforeReview(card: Flashcard, review: FlashcardReview): Flashcard {
  return {
    ...card,
    state: review.state,
    due: review.dueBefore,
    stability: review.stabilityBefore,
    difficulty: review.difficultyBefore,
    elapsedDays: review.elapsedDaysBefore,
    scheduledDays: review.scheduledDaysBefore,
    learningSteps: review.learningStepsBefore,
    reps: review.repsBefore,
    lapses: review.lapsesBefore,
    lastReview: review.lastReviewBefore,
    updatedAt: Date.now(),
  };
}

export function resetFlashcard(card: Flashcard, resetCounts = false): Flashcard {
  const now = Date.now();
  return {
    ...card,
    state: "new",
    due: now,
    stability: 0,
    difficulty: 0,
    elapsedDays: 0,
    scheduledDays: 0,
    learningSteps: 0,
    reps: resetCounts ? 0 : card.reps,
    lapses: resetCounts ? 0 : card.lapses,
    lastReview: null,
    position: now,
    suspended: false,
    buriedUntil: null,
    updatedAt: now,
  };
}

export function setFlashcardDue(card: Flashcard, due: number): Flashcard {
  return {
    ...card,
    state: card.state === "new" ? "review" : card.state,
    due,
    buriedUntil: null,
    updatedAt: Date.now(),
  };
}

export function flashcardRetrievability(card: Flashcard, options: FlashcardDeckOptions, now = Date.now()) {
  if (card.state === "new" || card.stability <= 0) return null;
  return scheduler(options).get_retrievability(toFsrsCard(card), new Date(now), false);
}

export function startOfNextDay(now = new Date()) {
  const next = new Date(now);
  next.setHours(24, 0, 0, 0);
  return next.getTime();
}

export function formatDueDifference(milliseconds: number) {
  const seconds = Math.max(0, Math.round(milliseconds / 1_000));
  if (seconds < 60) return `${Math.max(1, seconds)}s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d`;
  const months = Math.round(days / 30.4375);
  if (months < 12) return `${months}mês`;
  return `${(days / 365.25).toFixed(days < 730 ? 1 : 0)}a`;
}

function scheduler(options: FlashcardDeckOptions) {
  const parameters = options.fsrsParameters.length ? options.fsrsParameters : undefined;
  return fsrs({
    request_retention: options.desiredRetention,
    maximum_interval: options.maximumIntervalDays,
    w: parameters,
    enable_fuzz: true,
    enable_short_term: true,
    learning_steps: options.learningStepsMinutes.map(toStep),
    relearning_steps: options.relearningStepsMinutes.map(toStep),
  });
}

function nextFsrsSchedule(card: Flashcard, options: FlashcardDeckOptions, rating: FlashcardRating, now: number) {
  const steps = card.state === "relearning" ? options.relearningStepsMinutes : options.learningStepsMinutes;
  const hardGraduates = rating === Rating.Hard
    && (card.state === "learning" || card.state === "relearning")
    && steps.length > 0
    && card.learningSteps >= steps.length - 1;
  const result = scheduler(options).next(toFsrsCard(card), new Date(now), (hardGraduates ? Rating.Good : rating) as Grade);
  if (rating === Rating.Hard && !hardGraduates && (result.card.state === State.Learning || result.card.state === State.Relearning)) {
    result.card.learning_steps = Math.min(card.learningSteps + 1, Math.max(0, steps.length - 1));
  }
  return result;
}

function toStep(minutes: number): StepUnit {
  if (minutes > 0 && minutes % 1_440 === 0) return `${minutes / 1_440}d`;
  if (minutes > 0 && minutes % 60 === 0) return `${minutes / 60}h`;
  return `${Math.max(1, minutes)}m`;
}

function toFsrsCard(card: Flashcard): FsrsCard {
  return {
    due: new Date(card.due),
    stability: card.stability,
    difficulty: card.difficulty,
    elapsed_days: card.elapsedDays,
    scheduled_days: card.scheduledDays,
    learning_steps: card.learningSteps,
    reps: card.reps,
    lapses: card.lapses,
    state: toFsrsState(card.state),
    last_review: card.lastReview === null ? undefined : new Date(card.lastReview),
  };
}

function toFsrsState(state: FlashcardLearningState) {
  if (state === "learning") return State.Learning;
  if (state === "review") return State.Review;
  if (state === "relearning") return State.Relearning;
  return State.New;
}

function fromFsrsState(state: State): FlashcardLearningState {
  if (state === State.Learning) return "learning";
  if (state === State.Review) return "review";
  if (state === State.Relearning) return "relearning";
  return "new";
}

function ratingLabel(rating: FlashcardRating): RatingPreview["label"] {
  if (rating === 1) return "Novamente";
  if (rating === 2) return "Difícil";
  if (rating === 4) return "Fácil";
  return "Bom";
}
